// Resolves the image urls a movie's provider row actually carries, so a save
// can tell a provider image apart from a TMDB or WP image. Field names per
// docs/data-shapes/client/cli-movies.md:110-112, source variant names per
// docs/data-shapes/client/meta-pm-tmdb.md:192-200.
//
// store:       which programData store the source variant points at
// readAmbient: (row) => url | null
// readBackgrounds: (row) => string[]
// A null reader means the provider contributes nothing for that slot, so the
// slot can never be recognised as provider-owned — correct, because the merge
// has nothing to fall back to.
const PROVIDER_SOURCES = {
    Mars: {
        store: 'pyMovies',
        readAmbient: row => row.ambientURL,
        readBackgrounds: row => row.imageURLs,
    },
    MarsX: {
        store: 'marsxMovies',
        readAmbient: null,
        readBackgrounds: null,
    },
    Kinoheld: {
        store: 'khMovies',
        readAmbient: row => (row.heroImageUrls || [])[0],
        readBackgrounds: row => row.sceneImageUrls,
    },
    Compeso: {
        store: 'compesoMovies',
        readAmbient: row => (row.backdrop_urls || [])[0],
        readBackgrounds: row => row.backdrop_urls,
    },
    Cortec: {
        store: 'cortecMovies',
        readAmbient: row => (row.artworks || [])[0],
        readBackgrounds: row => row.artworks,
    },
    // dieselv2 rows carry no images at all — the merge fills images.* from the
    // linked tmdb row, so that is the value a save must compare against.
    // tmdbMovies backdrop_urls entries are objects ({url, iso_639_1, ...}), see
    // docs/data-shapes/client/samples/tmdb-movie.json:21-35. cli-movies.md:111
    // documents the merge as taking only the first 15 for backgrounds.
    DieselV2: {
        store: 'tmdbMovies',
        rowPkFrom: 'tmdb',
        readAmbient: row => (row.backdrop_urls || [])[0],
        readBackgrounds: row => (row.backdrop_urls || []).slice(0, 15),
    },
    Vista: {
        store: 'vistaMovies',
        readAmbient: row => row.backdrop_url,
        readBackgrounds: null,
    },
}

const urlOf = image => (image && typeof image === 'object' ? image.url : image)

// variant name -> store it reads from, derived from PROVIDER_SOURCES so a save
// can figure out which provider stores it actually needs to convert without
// duplicating this table (see WatchUpdateMovieItem.js).
export const PROVIDER_VARIANT_STORES = Object.keys(PROVIDER_SOURCES).reduce((acc, variant) => {
    acc[variant] = PROVIDER_SOURCES[variant].store
    return acc
}, {})

export const getProviderImageCandidates = (mapping, stores) => {
    const ambientUrls = []
    const backgroundUrlLists = []
    const source = (mapping && mapping.source) || []
    const allStores = stores || {}

    source.forEach(entry => {
        const variant = Object.keys(entry || {})[0]
        const config = PROVIDER_SOURCES[variant]
        if (!config) return

        const rowPk = config.rowPkFrom ? mapping[config.rowPkFrom] : entry[variant]
        const row = rowPk ? (allStores[config.store] || {})[rowPk] : null
        if (!row) return

        // Readers may return either plain url strings or {url} objects depending on
        // the store's own shape (e.g. tmdbMovies entries are objects, compesoMovies
        // entries are strings) — urlOf makes every variant shape-proof uniformly,
        // rather than only fixing the one variant known to return objects.
        if (config.readAmbient) {
            const ambientUrl = urlOf(config.readAmbient(row))
            if (ambientUrl) ambientUrls.push(ambientUrl)
        }

        if (config.readBackgrounds) {
            const urls = (config.readBackgrounds(row) || []).map(urlOf).filter(Boolean)
            if (urls.length > 0) backgroundUrlLists.push(urls)
        }
    })

    return { ambientUrls, backgroundUrlLists }
}

export const isProviderOwnedAmbient = (ambient, candidates) => {
    const url = urlOf(ambient)
    if (!url) return false
    return candidates.ambientUrls.includes(url)
}

export const isProviderOwnedBackgrounds = (backgrounds, candidates) => {
    if (!backgrounds || backgrounds.length === 0) return false
    const urls = backgrounds.map(urlOf)
    if (urls.some(url => !url)) return false
    return candidates.backgroundUrlLists.some(candidateUrls =>
        candidateUrls.length === urls.length && candidateUrls.every((url, i) => url === urls[i])
    )
}

export default getProviderImageCandidates
